'use strict';

import { findUrl, isValidUrl } from 'lib/helpers';
import { genKey, convertToRaw } from 'draft-js';
import { convertHtmlToDraftContent } from '../html';
const { parse } = require('markdown-to-ast');

const INLINE_STYLES = {
	'Strong': {
		type: 'BOLD',
		symbol: '__',
	},
	'Emphasis': {
		type: 'ITALIC',
		symbol: '*',
	},
	'Code': {
		type: 'CODE',
		symbol: '`',
	},
	'Delete': {
		type: 'STRIKETHROUGH',
		symbol: '~~',
	},
};

const BLOCK_STYLES = {
	List: 'unordered-list-item',
	Header1: 'header-one',
	Header2: 'header-two',
	Header3: 'header-three',
	Header4: 'header-four',
	Header5: 'header-five',
	Header6: 'header-six',
	CodeBlock: 'code-block',
	BlockQuote: 'blockquote',
};

const getBlockTypeForNode = (node) => {
	const style = node.type;
	const ordered = node.ordered;
	const depth = node.depth;
	if (style === 'List' && ordered) {
		return 'ordered-list-item';
	} else if (style === 'Header') {
		return BLOCK_STYLES[`${style}${depth}`];
	} else if (node.type === 'Paragraph' && node.children && node.children[0] && node.children[0].type === 'Image') { // eslint-disable-line max-len
		return 'atomic';
	}
	return BLOCK_STYLES[style];
};

const makeBlockJoiner = ({ open, close, escape = x => x }) => {
	const joiner = (lines) => {
		let opening, closing;

		for (let lineIndex = 0; lineIndex < lines.length; ++lineIndex) {
			const line = lines[lineIndex] && escape(lines[lineIndex]);
			const nextLine = lines[lineIndex + 1] && escape(lines[lineIndex + 1]);
			if (line && line.split(/\r?\n/g).length > 1) {
				// joined blocks
				continue;
			}
			if (typeof opening === 'undefined' && open(line, nextLine)) {
				opening = lineIndex;
				continue;
			}
			else if (typeof opening !== 'undefined' && typeof closing === 'undefined' && close(line, nextLine)) {
				closing = lineIndex;
				break;
			}
		}

		if (opening >= 0 && closing >= 0) {
			const block = lines.slice(opening, closing + 1).join('\n');
			return [
				...lines.slice(0, opening),
				block,
				...joiner(lines.slice(closing + 1))
			];
		}
		return lines;
	};
	return joiner;
}

const findCodeBlock = line => /^```/.test(line);
const joinCodeBlocks = makeBlockJoiner({
	open: findCodeBlock,
	close: findCodeBlock,
});


const findTableHtmlOpen = line => /^\s*<table>/.test(line);
const findTableHtmlEnd = line => /<\/table>\s*$/.test(line);
const joinTableHtmlBlocks = makeBlockJoiner({
	open: findTableHtmlOpen,
	close: findTableHtmlEnd,
});

const findListItem = line => /^\s*(\*|\-|\d+\.)\s/.test(line);
const joinListBlocks = makeBlockJoiner({
	open: findListItem,
	close: (line, nextLine) => !/\S/.test(line) || !nextLine || findQuote(line),
});

const findQuote = line => /^\s*> /.test(line);
const joinQuoteBlocks = makeBlockJoiner({
	open: findQuote,
	close: (line) => !findQuote(line),
});

const countColumns = line => {
	if (!line) return 0;
	if (findQuote(line)) return 0;
	if (findListItem(line)) return 0;
	const columns = line.split(/\|/g);
	if (!/\S/.test(columns[0])) columns.shift();
	if (!/\S/.test(columns[columns.length - 1])) columns.pop();
	return columns.length;
};
const isHeaderSpliter = line => /^\s*[\|\:\-\s]+\s*$/.test(line);
const joinTableBlocks = makeBlockJoiner({
	escape: line => line.replace(/\\\|/g, '&#124;'),
	open(line, nextLine) {
		const count = countColumns(line);
		if (count < 2) return false;
		if (countColumns(nextLine) != count) return false;
		if (!isHeaderSpliter(nextLine)) return false;
		return true;
	},
	close(line, nextLine) {
		return countColumns(line) >= 2 && countColumns(nextLine) < 2;
	}
});

/**
 * @param {string} markdown
 */
const splitMarkdownBlocks = (markdown) => {
	let blocks = markdown.split('\n');
	blocks = joinCodeBlocks(blocks);
	blocks = joinListBlocks(blocks);
	blocks = joinQuoteBlocks(blocks);
	blocks = joinTableBlocks(blocks);
	blocks = joinTableHtmlBlocks(blocks);
	return blocks;
};

const buildTableBlock = (node, entityMap) => {
	const text = '[表格]';
	const createCellDraftRaw = markdownRaw => {
		const ast = parse(markdownRaw.replace(/&#124;/g, '|'));
		const entityKey = {};
		const blocks = buildDraftBlocks(ast, entityKey);
		return {
			blocks,
			entityMap: entityKey,
		};
	};

	return {
		type: 'table',
		data: {
			aligns: node.align.map(align => ({
				key: genKey(),
				align,
			})),
			rows: node.children.map(row => ({
				key: genKey(),
				cells: row.children.map(cell => ({
					key: genKey(),
					raw: createCellDraftRaw(cell.raw),
				}))
			}))
		},
		text: '[表格]',
		inlineStyleRanges: [],
		entityRanges: []
	};
};

const buildHrBlock = (node, entityMap) => {
	return createAtomicBlock(entityMap, node.raw, 'HR', {});
};

const createAtomicBlock = (entityMap, text, entityType, entityData) => {
	const entityKey = Object.keys(entityMap).length;
	entityMap[entityKey] = {
		type: entityType,
		mutability: 'IMMUTABLE',
		data: entityData,
	};
	return {
		type: 'atomic',
		text,
		inlineStyleRanges: [],
		entityRanges: [{
			key: entityKey,
			offset: 0,
			length: text.length,
		}]
	};
}

const buildGalleryBlock = (gallery, entityMap) => {
	const { name, base, images } = gallery;
	return createAtomicBlock(entityMap, name, 'GALLERY', gallery);
};

const resolveGalleryCode = (node) => {
	if (node.type !== 'CodeBlock') return null;
	if (node.lang !== 'gallery') return null;
	try {
		const gallery = JSON.parse(node.value);
		if (!gallery.$galleryVersion) {
			throw new Error('Not gallery');
		}
		return gallery;
	}
	catch (err) {}
	return null;
};

const resolveAttachmentNode = (node) => {
	if (node.type !== 'BlockQuote') return null;
	const singleChild = node => node && node.children && node.children.length === 1 ? node.children[0] : null;
	const paragraph = singleChild(node);
	const link = singleChild(paragraph);
	const str = singleChild(link);
	if (str && /^#(.+)#$/.test(str.value)) {
		return {
			name: RegExp.$1,
			url: link.url
		}
	}
	return null;
};

const resolveVideoNode = (node) => {
	if (node.type !== 'BlockQuote') return null;
	const singleChild = node => node && node.children && node.children.length === 1 ? node.children[0] : null;
	const paragraph = singleChild(node);
	const image = singleChild(paragraph);

	if (image && /^video:\/\/(\w+)\/(\w+)$/.test(image.url)) {
		return {
			name: image.alt,
			videoPlatform: RegExp.$1,
			videoId: RegExp.$2,
		}
	}
	return null;
};

const resolveTexNode = (node) => {
	if (node.type !== 'BlockQuote') return null;
	// 忽略公式后的空格
	const singleChild = node => node && node.children &&
		(node.children.length === 1 ||
			(node.children.length === 2 &&
				node.children[1] &&
				node.children[1].type === 'Str' &&
				!(node.children[1].value && node.children[1].value.trim())
			)
		) ? node.children[0] : null;
	const paragraph = singleChild(node);
	const code = singleChild(paragraph);

	if (!code || code.type !== 'Code') return null;

	if (/^\$(.+)\$$/.test(code.value)) {
		return { teX: RegExp.$1 };
	}
	return null;
};

const buildAttachmentBlock = (attachment, entityMap) => {
	const { name, url } = attachment;
	return createAtomicBlock(entityMap, name, 'ATTACHMENT', attachment);
};

const buildVideoBlock = (video, entityMap) => {
	const { name, videoPlatform, videoId } = video;
	return createAtomicBlock(entityMap, name, 'VIDEO', video);
}

const buildDraftBlocks = (ast, entityMap, depth = 0, options) => {
	let block = {
		key: genKey(),
		type: 'unstyled',
		text: '',
		depth,
		inlineStyleRanges: [],
		entityRanges: [],
	};
	let listMode = false;
	let listItemConsumed = false;
	let blockquoteMode = false;
	const firstChild = ast.children[0];
	if (firstChild) {
		if (firstChild.type === 'Table') {
			return [buildTableBlock(firstChild, entityMap)];
		}
		if (firstChild.type === 'HorizontalRule') {
			return [buildHrBlock(firstChild, entityMap)];
		}
		const gallery = resolveGalleryCode(firstChild);
		if (gallery) {
			return [buildGalleryBlock(gallery, entityMap)];
		}
		const attachment = resolveAttachmentNode(firstChild);
		if (attachment) {
			return [buildAttachmentBlock(attachment, entityMap)];
		}
		const video = resolveVideoNode(firstChild);
		if (video) {
			return [buildVideoBlock(video, entityMap)];
		}
		const tex = resolveTexNode(firstChild);
		if (tex) {
			return [buildTexBlock(tex.teX)];
		}
		block.type = getBlockTypeForNode(firstChild) || block.type;
		listMode = firstChild.type === 'List';
		blockquoteMode = firstChild.type === 'BlockQuote';
	};
	const restBlocks = [];

	const addData = (key, value) => {
		block.data = block.data || {};
		block.data[key] = value;
	};

	const addInlineStyleRange = (offset, length, style) => {
		block.inlineStyleRanges.push({ offset, length, style });
	};

	const getRawLength = children => children.reduce(
		(prev, current) => prev + (current.value ?
			current.value.length :
			current.children ? getRawLength(current.children) : 0
		), 0);

	const addLink = ({ url, children }) => {
		const entityKey = Object.keys(entityMap).length;
		if (/^\/\//.test(url)) {
			url = 'https:' + url;
		}
		// 自动识别的 URL 没有处理中文情况
		const isAutoLink = children && children[0] && url === children[0].raw;
		if (isAutoLink) {
			url = findUrl(url);
		}
		if (url && isValidUrl(url, true)) {
			entityMap[entityKey] = {
				type: 'LINK',
				mutability: 'MUTABLE',
				data: { url: encodeURI(url) }
			};
			block.entityRanges.push({
				key: entityKey,
				offset: block.text.length,
				length: isAutoLink ? url.length : getRawLength(children),
			});
		}
	};

	const addImage = (child, holder) => {
		const entityKey = Object.keys(entityMap).length;
		let alt = child.alt || child.url.split('/').pop();
		alt = `[${alt || '图片'}]`;
		entityMap[entityKey] = {
			type: 'IMAGE',
			mutability: 'IMMUTABLE',
			data: {
				imageUrl: child.url,
				name: child.alt || null,
			}
		};
		block.entityRanges.push({
			key: entityKey,
			offset: block.text.length,
			length: alt.length,
		});
		return alt;
	};

	const addFormua = (teX, text) => {
		const entityKey = Object.keys(entityMap).length;
		entityMap[entityKey] = {
			type: 'INLINETEX',
			mutability: 'IMMUTABLE',
			data: { teX }
		};
		block.entityRanges.push({
			key: entityKey,
			offset: block.text.length,
			length: text.length,
		});
	};

	const parseChildren = (child, inlineStyle) => {
		let imageAlt = null;
		let codeLang = null;

		switch (child.type) {
			case 'Link':
				addLink(child);
				break;
			case 'Image':
				imageAlt = addImage(child);
				break;
			case 'CodeBlock':
				addData('syntex', child.lang);
				break;
			default:
			// do nothing
		}

		// not leaf
		if (child.children) {
			if (inlineStyle) {
				// Bold/Italic/Delete/Code style nodes
				const rawLength = getRawLength(child.children);
				addInlineStyleRange(block.text.length, rawLength, inlineStyle.type);
			}

			const grandInlineStyle = INLINE_STYLES[child.type];
			for (let i = 0; i < child.children.length; i++) {
				const grandChild = child.children[i];
				if (listMode) {
					switch (grandChild.type) {
					case 'List':
						restBlocks.push(...buildDraftBlocks({
							type: "Document",
							children: [grandChild],
						}, entityMap, depth + 1));
						break;
					case 'ListItem':
						if (listItemConsumed) {
							restBlocks.push(...buildDraftBlocks({
								type: "Document",
								children: [{
									type: "List",
									ordered: firstChild.ordered,
									children: [grandChild]
								}],
							}, entityMap, depth));
						}
						else {
							listItemConsumed = true;
							parseChildren(grandChild, grandInlineStyle);
						}
						break;
					default:
						parseChildren(grandChild, grandInlineStyle);
						break;
					}
				}
				else {
					parseChildren(grandChild, grandInlineStyle);
				}
				if (blockquoteMode && grandChild.type === 'ListItem' && child.children[i + 1]) {
					block.text += '\n';
				}
			}
		}
		// leaf
		else {
			if (inlineStyle && typeof child.value === 'string') {
				addInlineStyleRange(block.text.length, child.value.length, inlineStyle.type);
			}
			switch (child.type) {
				case 'Code':
					if (/^\$(.+)\$$/.test(child.value)) {
						const teX = RegExp.$1;
						addFormua(teX, teX);
						block.text += teX;
					}
					else {
						child.value = child.value.replace(/&grave;|&#96;|&#x60;/ig, '`');
						addInlineStyleRange(block.text.length, child.value.length, 'CODE');
						block.text += child.value;
					}
					break;
				case 'Image':
					block.text += imageAlt;
					break;
				case 'Html':
					if (/^<br(\s*\/)?>$/.test(child.value)) {
						block.text += '\n';
						break;
					}
					if (/^\s*<table>[\s\S]*<\/table>\s*$/.test(child.value)) {
						try {
							const content = convertHtmlToDraftContent(child.value, options);
							const raw = convertToRaw(content);
							const tableBlock = raw.blocks[0];
							tableBlock.text = '表格';
							tableBlock.data.rows = tableBlock.data.rows.map(row => ({
								...row,
								cells: row.cells.map(cell => ({
									...cell,
									raw: cell.raw ? buildDraftRawFromMarkdown(cell.raw.blocks[0].text) : null,
								}))
							}));
							block = tableBlock;
							break;
						}
						catch (err) {
							throw new Error('Markdown 中包含通过 HTML 声明的表格，但是转换 HTML 失败。' + err.message);
						}
					}
				default:
					block.text += child.value;
					break;
			}
		}
	};

	for (let child of ast.children) {
		parseChildren(child, INLINE_STYLES[child.type]);
	}

	return [
		block,
		...restBlocks,
	];
};

const buildTexBlock = (teX) => {
	return {
		type: 'atomic',
		text: teX,
		data: { mathjax: true, teX },
		inlineStyleRanges: [],
		entityRanges: []
	};
};

function purify(markdown) {
	// 暂时忽略颜色语法，等编辑器支持再补上
	markdown = markdown.replace(/!!#?\w{3,6}\s(.+?)!!/g, '$1');

	// 去除软换行
	// markdown = markdown.replace(/<br\s*\/>/g, '\n');

	return markdown;
}

/**
 * @param {string} markdown
 */
export function parseMarkdownToAstList(markdown) {
	const paragraphs = splitMarkdownBlocks(purify(markdown));

	const removeLocAndRange = (ast) => {
		if (!ast) {
			return ast;
		}
		delete ast.loc;
		delete ast.range;
		if (Array.isArray(ast.children)) {
			for (let child of ast.children) {
				removeLocAndRange(child);
			}
		}
		return ast;
	};

	return (paragraphs
		.map(paragraph => parse(paragraph))
		.filter(ast => ast.raw && ast.raw.trim())
		.map(ast => removeLocAndRange(ast))
	);
}

export function buildDraftRawFromAstList(astList, options) {
	let blocks = [];
	let entityMap = {};

	for (let ast of astList) {
		blocks = blocks.concat(buildDraftBlocks(ast, entityMap, 0, options));
	}

	// add a default value
	// not sure why that's needed but Draftjs convertToRaw fails without it
	if (Object.keys(entityMap).length === 0) {
		entityMap = {
			data: '',
			mutability: '',
			type: ''
		};
	}

	const isEmptyBlock = block => block && block.type === 'unstyled' && !/\S/.test(block.text);
	blocks = blocks.filter(block => !isEmptyBlock(block));

	if (!blocks.length) {
		// must has at least one block
		blocks.push({
			key: genKey(),
			type: 'unstyled',
			text: '',
			inlineStyleRanges: [],
			entityRanges: []
		});
	}

	return {
		blocks,
		entityMap
	};
}

/**
 * @param {string} markdown
 */
export function buildDraftRawFromMarkdown(markdown, options) {
	return buildDraftRawFromAstList(parseMarkdownToAstList(markdown), options);
}



// WEBPACK FOOTER //
// ./lib/convert/markdown/convertMarkdownToDraftRaw.js